import { ISocialNetworkRepository } from '../../domain/repositories/social-network.repository.interface';
import { IHttpClient } from '../../application/interfaces/http-client.interface';
import { SocialNetwork } from '../../domain/entities/social-network.entity';
import { CreateSocialNetworkDTO } from '../../application/dtos/social-networks/create-social-network.dto';
import { UpdateSocialNetworkDTO } from '../../application/dtos/social-networks/update-social-network.dto';
import { SocialNetworkResponseDTO } from '../../application/dtos/social-networks/social-network-response.dto';

interface ApiResponse<T> {
  success: boolean;
  message?: string;
  data: T;
}

export class HttpSocialNetworkRepository implements ISocialNetworkRepository {
  constructor(private readonly httpClient: IHttpClient) {}

  public async getAll(filters?: { activeOnly?: boolean }): Promise<SocialNetwork[]> {
    const query = filters?.activeOnly ? '?activeOnly=true' : '';
    const response = await this.httpClient.get<ApiResponse<SocialNetworkResponseDTO[]>>(
      `/social-networks${query}`
    );
    if (!response || !response.success || !Array.isArray(response.data)) {
      throw new Error('Formato de respuesta inválido al consultar redes sociales.');
    }
    return response.data.map((item) => this.mapToDomain(item));
  }

  public async getById(id: string): Promise<SocialNetwork> {
    const response = await this.httpClient.get<ApiResponse<SocialNetworkResponseDTO>>(
      `/social-networks/${id}`
    );
    if (!response || !response.success || !response.data) {
      throw new Error('No se pudo obtener la red social especificada.');
    }
    return this.mapToDomain(response.data);
  }

  public async create(data: CreateSocialNetworkDTO): Promise<SocialNetwork> {
    const response = await this.httpClient.post<ApiResponse<SocialNetworkResponseDTO>>(
      '/social-networks',
      data
    );
    if (!response || !response.success || !response.data) {
      throw new Error('Error al crear la red social en el servidor.');
    }
    return this.mapToDomain(response.data);
  }

  public async update(id: string, data: UpdateSocialNetworkDTO): Promise<SocialNetwork> {
    const response = await this.httpClient.put<ApiResponse<SocialNetworkResponseDTO>>(
      `/social-networks/${id}`,
      data
    );
    if (!response || !response.success || !response.data) {
      throw new Error('Error al actualizar la red social.');
    }
    return this.mapToDomain(response.data);
  }

  public async changeStatus(id: string, isActive: boolean): Promise<SocialNetwork> {
    const response = await this.httpClient.patch<ApiResponse<SocialNetworkResponseDTO>>(
      `/social-networks/${id}/status`,
      { isActive }
    );
    if (!response || !response.success || !response.data) {
      throw new Error('Error al cambiar el estado de la red social.');
    }
    return this.mapToDomain(response.data);
  }

  public async reorder(items: { id: string; displayOrder: number }[]): Promise<void> {
    const response = await this.httpClient.patch<ApiResponse<null>>(
      '/social-networks/reorder',
      { items }
    );
    if (!response || !response.success) {
      throw new Error('Error al actualizar el orden de las redes sociales.');
    }
  }

  public async delete(id: string): Promise<void> {
    const response = await this.httpClient.delete<ApiResponse<null>>(`/social-networks/${id}`);
    if (!response || !response.success) {
      throw new Error('Error al eliminar la red social.');
    }
  }

  private mapToDomain(dto: SocialNetworkResponseDTO): SocialNetwork {
    const raw = dto as any;
    return SocialNetwork.create({
      id: raw.id ?? raw._id,
      name: raw.name ?? raw._name ?? '',
      type: raw.type ?? raw._type ?? '',
      url: raw.url ?? raw._url ?? '',
      icon: raw.icon ?? raw._icon,
      isActive: raw.isActive !== undefined ? raw.isActive : (raw._isActive !== undefined ? raw._isActive : true),
      displayOrder: raw.displayOrder ?? raw._displayOrder ?? 0,
      createdAt: raw.createdAt ?? raw._createdAt,
      updatedAt: raw.updatedAt ?? raw._updatedAt,
    });
  }
}
